import {
  useCreateFeedbackFormula,
  useUpdateFeedbackFormula,
} from '@/hooks/useSwr';
import {
  FeedbackFormulaAggregationType,
  FeedbackFormulaCreateBody,
  FeedbackFormulaSchema,
  FeedbackFormulaUpdateBody,
  FeedbackFormulaWeightedVariable,
} from '@/types/schema';

interface ScoreRow extends FeedbackFormulaWeightedVariable {
  id: string;
}

export const useCompositeScoreCrud = ({
  onSaved,
  onClose,
  datasetId,
  sessionId,
  initialFormula,
}: {
  onSaved?: (formula: FeedbackFormulaSchema) => void;
  onClose: () => void;
  datasetId?: string;
  sessionId?: string;
  initialFormula?: FeedbackFormulaSchema;
}) => {
  const onSuccess = (data: FeedbackFormulaSchema) => {
    onSaved?.(data);
    onClose();
  };

  const createFormula = useCreateFeedbackFormula({
    onSuccess,
  });

  const updateFormula = useUpdateFeedbackFormula({
    onSuccess,
  });

  async function handleSave(data: {
    name: string;
    method: FeedbackFormulaAggregationType;
    rows: ScoreRow[];
  }) {
    const parts = data.rows
      .filter((r) => r.key.trim())
      .map((r) => ({
        part_type: 'weighted_key' as const,
        weight: r.weight,
        key: r.key.trim(),
      }));
    const aggregation_type: FeedbackFormulaCreateBody['aggregation_type'] =
      data.method === 'sum' ? 'sum' : 'avg';

    if (initialFormula?.id) {
      const body: FeedbackFormulaUpdateBody = {
        feedback_key: data.name,
        aggregation_type,
        formula_parts: parts,
      };
      await updateFormula.trigger({
        templateUrlParams: { id: initialFormula.id },
        json: body,
      });
    } else {
      const body: FeedbackFormulaCreateBody = {
        dataset_id: datasetId,
        session_id: sessionId,
        feedback_key: data.name,
        aggregation_type,
        formula_parts: parts,
      };
      await createFormula.trigger({ json: body });
    }
  }

  return {
    handleSave,
    isMutating: createFormula.isMutating || updateFormula.isMutating,
    error: createFormula.error || updateFormula.error,
  };
};
